import { UpdateAssetDTO } from '../../../../../base/models/models.swagger.generated';
import { ProductAssetsService } from '../../product-assets.service';
import { Component, ViewChild, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DialogComponent, DialogModule } from '@syncfusion/ej2-angular-popups';
import { AssetDTOStargate, AssetType } from 'src/app/base/models/models.swagger.generated';
import { ToastService } from 'src/app/base/services/toast.service';
import { ErrorType } from 'src/app/core/routing/error-types';
import { AssetUploadEditorFormType } from '../../../shared/asset-upload-editor/asset-upload-editor-form-type';
import { TranslateModule } from '@ngx-translate/core';
import { AssetEditTabGroupComponent } from "../../../shared/asset-edit-tab-group/asset-edit-tab-group.component";


@Component({
    selector: 'app-product-assets-single-edit',
    templateUrl: './product-assets-single-edit.component.html',
    styleUrls: ['./product-assets-single-edit.component.scss'],
    imports: [DialogModule, TranslateModule, AssetEditTabGroupComponent],
    providers: [ProductAssetsService]
})
export class ProductAssetsSingleEditComponent implements OnInit {

  @ViewChild("editProductAssetDialog")
  public dialog: DialogComponent;

  @ViewChild("assetEditTabGroupComponent")
  public assetEditTabGroupComponent: AssetEditTabGroupComponent;

  @Output()
  public OnClose: EventEmitter<any> = new EventEmitter<any>();

  public assetUploadEditorForm: FormGroup<AssetUploadEditorFormType>;

  public showImageEditor: boolean = false;

  constructor(
    private fb: FormBuilder,
    private toastService: ToastService,
    private productAssetsService: ProductAssetsService,
  ) { }

  public ngOnInit(): void {
    this.assetUploadEditorForm = this.fb.group<AssetUploadEditorFormType>({
      id: this.fb.control<number>(null),
      publicName: this.fb.control<string>(null, [Validators.required, Validators.pattern(/^[a-z0-9_-]+$/)]),
      originalFileName: this.fb.control<string>({ value: null, disabled: true }),
      fileExtension: this.fb.control<string>({ value: null, disabled: true }),
      fileLink: this.fb.control<string>(null),
      language: this.fb.control<string>(null, [Validators.required]),
      type: this.fb.control<AssetType>(null, [Validators.required]),
      isKey: this.fb.control<boolean>(false, [Validators.required]),
      shopIds: this.fb.control<string[]>(['LITTLEBIT'], [Validators.required]),
      base64FileData: this.fb.control<string>(null, [Validators.required]),
      totalSize: this.fb.control<number>(null),
      localFilePath: this.fb.control<string>({ value: null, disabled: true }),
      altText: this.fb.control<string>(null),
      replaceProductLogos: this.fb.control<boolean>(false),
      replaceCustomizedLogos: this.fb.control<boolean>(false),
      intermediateFileInfo: this.fb.control<any>(null)
    });
  }

  public editAsset(asset: AssetDTOStargate) {
    this.setValidators();
    this.fillForm(asset);
    this.dialog.show(false);
  }

  public onCancel() {
    this.dialog.hide();
    this.resetDialog();
  }

  public onOpen(args: any): void {
    //Preventing the default dialog focus
    args.preventFocus = true;
  }

  public onSave() {
    if (!this.assetEditTabGroupComponent.canSave()) {
      return;
    }

    if (!this.assetUploadEditorForm.valid) {
      this.assetUploadEditorForm.markAllAsTouched();
      this.assetUploadEditorForm.markAsDirty();
      if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
        this.toastService.showWarning('i18n.domain.assets.product-assets.edit-asset.form-invalid.title',
          'i18n.domain.assets.product-assets.edit-asset.form-invalid.message', null);
      }
      return;
    }
    var extension = this.assetUploadEditorForm.controls.fileExtension.value?.toLowerCase();
    if ((extension == "jpg" ||
      extension == "jpeg" ||
      extension == "png") &&
      this.assetEditTabGroupComponent.assetImageEditorComponent?.imageEditorObj) {
      this.assetEditTabGroupComponent.getImageDataFromCanvas();
    }

    this.productAssetsService.updateProductAssets(this.fillUpdateAssetsDTO()).subscribe({
      next: () => {
        this.OnClose.emit();
        this.dialog.hide();
        this.resetDialog();
      }
    });
  }

  public setScreenSize(isFullScreen: boolean) {
    isFullScreen ? this.dialog.show(true) : this.dialog.show(false);
  }

  private resetDialog() {
    this.assetEditTabGroupComponent.resetTabGroup();

    this.assetUploadEditorForm.setValue({
      id: null,
      publicName: null,
      originalFileName: null,
      fileExtension: null,
      fileLink: null,
      language: 'ENU',
      type: null,
      isKey: false,
      shopIds: ['LITTLEBIT'],
      base64FileData: null,
      totalSize: null,
      localFilePath: null,
      altText: null,
      replaceProductLogos: false,
      replaceCustomizedLogos: false,
      intermediateFileInfo: null
    });
    this.assetUploadEditorForm.controls.base64FileData.setValidators([Validators.required]);
    this.assetUploadEditorForm.controls.base64FileData.updateValueAndValidity();
    this.assetUploadEditorForm.markAsUntouched();
    this.assetUploadEditorForm.markAsPristine();
    this.assetUploadEditorForm.updateValueAndValidity();
  }

  private fillUpdateAssetsDTO(): UpdateAssetDTO {
    return new UpdateAssetDTO({
      id: this.assetUploadEditorForm.controls.id.value,
      publicName: this.assetUploadEditorForm.controls.publicName.value,
      originalFileName: this.assetUploadEditorForm.controls.localFilePath.value,
      language: this.assetUploadEditorForm.controls.language.value,
      type: this.assetUploadEditorForm.controls.type.value,
      shopIds: this.assetUploadEditorForm.controls.shopIds.value,
      base64FileData: this.assetUploadEditorForm.controls.base64FileData.value,
      altText: this.assetUploadEditorForm.controls.altText.value
    });
  }

  private fillForm(asset: AssetDTOStargate) {
    this.assetUploadEditorForm.setValue({
      id: asset.id,
      publicName: asset.publicName,
      originalFileName: asset.originalFileName,
      fileExtension: asset.fileExtension,
      fileLink: asset.fileLink,
      language: asset.languageCodeIso3 == '' ? null : asset.languageCodeIso3,
      type: asset.type,
      isKey: false,
      shopIds: asset.shops.map(shop => shop.shopId),
      base64FileData: null,
      totalSize: null,
      localFilePath: null,
      altText: asset.altText,
      replaceProductLogos: false,
      replaceCustomizedLogos: false,
      intermediateFileInfo: null
    });

    this.assetUploadEditorForm.controls.base64FileData.setValidators([]);
    this.assetUploadEditorForm.controls.base64FileData.updateValueAndValidity();
  }

  private setValidators() {
    this.assetUploadEditorForm.controls.id.setValidators([Validators.required]);
    this.assetUploadEditorForm.controls.isKey.clearValidators();
    this.assetUploadEditorForm.controls.isKey.disable();
    this.assetUploadEditorForm.controls.id.updateValueAndValidity();
    this.assetUploadEditorForm.controls.isKey.updateValueAndValidity();
  }
}
